import type { TMessage } from "@asurion-core/headless-chat-web";

import { getMessageDataType, getMessageText, MESSAGE_DATA_TYPES, TSource } from "@asurion-core/headless-chat-web";

import type {
    GuideDropdownInput,
    GuideMessage,
    GuideSelectionMessage,
    GuideStep,
    GuideStepSummary,
    GuideTextInput,
    GuideTextMessage,
} from "./types";

export const isUiMetadataMessage = (message: TMessage) => {
    const isTextMessage = getMessageDataType(message) === MESSAGE_DATA_TYPES.TEXT;
    const hasCustomData = (message as GuideTextMessage)?.data?.customData;

    return isTextMessage && hasCustomData;
};

export const isGuideSelectionMessage = (message: TMessage) => {
    const messageType = getMessageDataType(message) as MESSAGE_DATA_TYPES | undefined;
    const hasGuideSelection = !!(message as GuideSelectionMessage)?.data?.guideSelection;
    return messageType === MESSAGE_DATA_TYPES.CUSTOM && hasGuideSelection;
};

// TODO: for v2, Simplr team will add a better way for us to handle sending us
// ui related metadata from the logic builder
// [EWS-1094](https://solutonashville.atlassian.net/browse/EWS-1094)
export const isValidGuideMessage = (message: TMessage) => {
    const messageType = getMessageDataType(message) as MESSAGE_DATA_TYPES | undefined;
    switch (messageType) {
        case MESSAGE_DATA_TYPES.TEXT:
            return message.source === TSource.BOT && !isUiMetadataMessage(message);
        case MESSAGE_DATA_TYPES.CUSTOM:
            return !!(message as GuideSelectionMessage).data?.guideSelection;
        default:
            return false;
    }
};

const getActionsFromMessage = (message: GuideTextMessage) => {
    const quickInput = message.data?.quickInput;
    if (!quickInput) return [];
    switch (quickInput.type) {
        case "BUTTONS":
            return quickInput.options;
        case "DROPDOWN": {
            const dropdownInput = quickInput as GuideDropdownInput;
            return dropdownInput.action ? [dropdownInput.action] : quickInput.options;
        }
        case "INPUT": {
            const textInput = quickInput as GuideTextInput;
            return textInput.action ? [textInput.action] : quickInput.options;
        }
    }
};

export const getCompletedStepsFromMessages = (messages: TMessage[]) => {
    const completedSteps = messages
        .filter((message) => message.source === TSource.BOT && !isUiMetadataMessage(message))
        .slice(0, -1);
    return completedSteps.map((message, index) => getCurrentStepIdFromMessage(message, index));
};

const getCurrentStepIdFromMessage = (message: TMessage, index: number) => {
    // currentStepId for analytics & logging
    // if stepId (custom property) is not defined in logic builder, use the step count as the id
    const textMessage = message as unknown as GuideMessage;
    return textMessage.data?.stepId ?? `Step${index + 1}`;
};

export const getGuideCardPropsFromTextMessage = (message: TMessage): GuideStep => {
    const textMessage = message as unknown as GuideTextMessage;
    const currentStep = getMessageText(message) as string;
    const alternateText = textMessage.data?.quickInput?.alternateText;
    const actions = getActionsFromMessage(textMessage);
    const quickInput = textMessage.data?.quickInput;
    const dropdownInput = quickInput?.type === "DROPDOWN" ? (quickInput as GuideDropdownInput) : undefined;
    const textInput = quickInput?.type === "INPUT" ? (quickInput as GuideTextInput) : undefined;

    const hasPreviousSteps = !(textMessage.data?.isFirstStep ?? false);

    const progress = textMessage.progress?.percent ?? 0;

    return {
        actions,
        alternateText,
        currentStep,
        dropdownInput,
        hasPreviousSteps,
        progress,
        prompt: textMessage.data?.assistPrompt,
        showAskAssist: textMessage.data?.showAskAssist ?? false,
        showDeviceConfirmation: textMessage.data?.showDeviceConfirmation ?? false,
        textInput,
    };
};

export const getGuideStepSummary = (message: TMessage | undefined): GuideStepSummary | undefined => {
    if (!message || getMessageDataType(message) !== MESSAGE_DATA_TYPES.TEXT) return undefined;
    const textMessage = message as unknown as GuideTextMessage;

    return {
        label: textMessage.data?.tag ?? textMessage.data?.stepId,
        progress: textMessage.progress?.percent ?? 0,
    };
};

export const getPropsFromGuideSelectionMessage = (message: TMessage) => {
    const customMessageData = (message as unknown as GuideSelectionMessage).data;
    const currentStepId = customMessageData?.stepId ?? "GuideSelection";

    return {
        currentStepId,
    };
};

export const getSupportedDevicesFromMessage = (message: TMessage): string[] | undefined => {
    const supportedDevices = (message as unknown as GuideTextMessage)?.data?.supportedDevices;
    return Array.isArray(supportedDevices) ? supportedDevices : undefined;
};

export const getUnsupportedStepIdFromMessage = (message: TMessage): string | undefined =>
    (message as unknown as GuideTextMessage)?.data?.unsupportedStepId;

export const getGuideAnalyticDataFromMessage = (message?: TMessage, messages?: TMessage[]) => {
    if (!message || !messages) return { completedSteps: "", completedStepCount: 0, currentStepId: "" };

    // includes all messages from the bot except for the current message
    const completedSteps = getCompletedStepsFromMessages(messages);
    const completedStepsStringified = completedSteps.join(", ");
    const completedStepCount = completedSteps.length;
    const currentStepId = getCurrentStepIdFromMessage(message, completedStepCount);

    return {
        currentStepId,
        completedStepCount,
        completedSteps: completedStepsStringified,
    };
};
